import React, { useRef, useState } from "react";
import axios from "axios";
import { Link as LinkIcon, Loader2, Check, AlertTriangle, Type, Camera, Upload, X, Sparkles } from "lucide-react";
import { API } from "@/lib/constants";

const TAB_LABELS = {
  en: { url: "URL", text: "Paste text", photo: "Photo", url_hint: "Paste a link from Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne or Gumtree.", text_hint: "Works with any source: Facebook, WhatsApp, LinkedIn, email…", photo_hint: "Upload a screenshot from anywhere: Facebook group, WhatsApp, agency board…", photo_cta: "Choose an image", photo_drop: "or drag & drop here", photo_extracting: "Extracting text with AI…", photo_success: "Text extracted successfully! Review below and adjust if needed.", change_image: "Change image", photo_types: "JPG, PNG or WebP · max 10 MB", url_label: "Job URL", url_placeholder: "Paste your Seek, Jora, Indeed, BackpackerJB link…", platform_reliable: "auto-fetch works well ✨", platform_may_block: "may block scraping — we'll ask you to paste if so", supported: "Supported", blocked_title: "This site is blocking automated fetching", blocked_body: "No worries! Just paste the text (tab \"Paste text\") or upload a screenshot (tab \"Photo\") — works the same." },
  fr: { url: "URL", text: "Coller texte", photo: "Photo", url_hint: "Colle un lien depuis Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne ou Gumtree.", text_hint: "Fonctionne avec toute source : Facebook, WhatsApp, LinkedIn, email…", photo_hint: "Upload un screenshot depuis n'importe où : groupe Facebook, WhatsApp, agence…", photo_cta: "Choisir une image", photo_drop: "ou glisse-dépose ici", photo_extracting: "Extraction du texte par l'IA…", photo_success: "Texte extrait avec succès ! Vérifie ci-dessous et ajuste si besoin.", change_image: "Changer d'image", photo_types: "JPG, PNG ou WebP · max 10 Mo", url_label: "Lien de l'annonce", url_placeholder: "Colle ton lien Seek, Jora, Indeed, BackpackerJB…", platform_reliable: "récupération auto fiable ✨", platform_may_block: "peut bloquer — on te demandera de coller si besoin", supported: "Supporté", blocked_title: "Ce site bloque la récupération automatique", blocked_body: "Pas de souci ! Colle simplement le texte (onglet \"Coller texte\") ou upload un screenshot (onglet \"Photo\") — ça marche pareil." },
  de: { url: "URL", text: "Text einfügen", photo: "Foto", url_hint: "Füge einen Link von Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne oder Gumtree ein.", text_hint: "Funktioniert mit jeder Quelle: Facebook, WhatsApp, LinkedIn, E-Mail…", photo_hint: "Lade einen Screenshot hoch von überall: Facebook-Gruppe, WhatsApp, Agentur…", photo_cta: "Bild auswählen", photo_drop: "oder hier ablegen", photo_extracting: "Text wird per KI extrahiert…", photo_success: "Text erfolgreich extrahiert! Überprüfe unten und passe an, falls nötig.", change_image: "Bild ändern", photo_types: "JPG, PNG oder WebP · max 10 MB", url_label: "Job-URL", url_placeholder: "Füge deinen Seek-, Jora-, Indeed-, BackpackerJB-Link ein…", platform_reliable: "Auto-Abruf funktioniert gut ✨", platform_may_block: "kann Scraping blockieren — wir fragen dich zu kopieren", supported: "Unterstützt", blocked_title: "Diese Seite blockiert das automatische Abrufen", blocked_body: "Kein Problem! Füge einfach den Text ein (Tab \"Text einfügen\") oder lade einen Screenshot hoch (Tab \"Foto\")." },
  it: { url: "URL", text: "Incolla testo", photo: "Foto", url_hint: "Incolla un link da Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne o Gumtree.", text_hint: "Funziona con qualsiasi fonte: Facebook, WhatsApp, LinkedIn, email…", photo_hint: "Carica uno screenshot da qualsiasi fonte: gruppo Facebook, WhatsApp, agenzia…", photo_cta: "Scegli un'immagine", photo_drop: "o trascina qui", photo_extracting: "Estrazione testo con IA…", photo_success: "Testo estratto con successo! Controlla sotto e modifica se necessario.", change_image: "Cambia immagine", photo_types: "JPG, PNG o WebP · max 10 MB", url_label: "URL dell'annuncio", url_placeholder: "Incolla il tuo link Seek, Jora, Indeed, BackpackerJB…", platform_reliable: "recupero auto affidabile ✨", platform_may_block: "può bloccare lo scraping — ti chiederemo di incollare", supported: "Supportato", blocked_title: "Questo sito blocca il recupero automatico", blocked_body: "Nessun problema! Incolla il testo (tab \"Incolla testo\") o carica uno screenshot (tab \"Foto\")." },
  es: { url: "URL", text: "Pegar texto", photo: "Foto", url_hint: "Pega un enlace de Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne o Gumtree.", text_hint: "Funciona con cualquier fuente: Facebook, WhatsApp, LinkedIn, correo…", photo_hint: "Sube una captura desde cualquier lugar: grupo Facebook, WhatsApp, agencia…", photo_cta: "Elegir imagen", photo_drop: "o arrastra aquí", photo_extracting: "Extrayendo texto con IA…", photo_success: "¡Texto extraído con éxito! Revisa abajo y ajusta si es necesario.", change_image: "Cambiar imagen", photo_types: "JPG, PNG o WebP · máx 10 MB", url_label: "URL del anuncio", url_placeholder: "Pega tu enlace de Seek, Jora, Indeed, BackpackerJB…", platform_reliable: "recuperación auto fiable ✨", platform_may_block: "puede bloquear el scraping — te pediremos pegar", supported: "Soportado", blocked_title: "Este sitio bloquea la recuperación automática", blocked_body: "¡No hay problema! Pega el texto (pestaña \"Pegar texto\") o sube una captura (pestaña \"Foto\")." },
  pt: { url: "URL", text: "Colar texto", photo: "Foto", url_hint: "Cola um link do Seek, Indeed, Jora, BackpackerJB, Workforce AU, CareerOne ou Gumtree.", text_hint: "Funciona com qualquer fonte: Facebook, WhatsApp, LinkedIn, email…", photo_hint: "Envia uma captura de qualquer lugar: grupo Facebook, WhatsApp, agência…", photo_cta: "Escolher imagem", photo_drop: "ou arrasta aqui", photo_extracting: "A extrair texto com IA…", photo_success: "Texto extraído com sucesso! Verifica abaixo e ajusta se necessário.", change_image: "Mudar imagem", photo_types: "JPG, PNG ou WebP · máx 10 MB", url_label: "URL do anúncio", url_placeholder: "Cola o teu link do Seek, Jora, Indeed, BackpackerJB…", platform_reliable: "recuperação auto fiável ✨", platform_may_block: "pode bloquear o scraping — vamos pedir para colar", supported: "Suportado", blocked_title: "Este site bloqueia a recuperação automática", blocked_body: "Sem problema! Cola o texto (separador \"Colar texto\") ou carrega uma captura (separador \"Foto\")." },
};

export function Step2Job({ t, jobText, setJobText, jobUrl, setJobUrl, jobMeta, setJobMeta, lang = "en" }) {
  const [tab, setTab] = useState("url"); // url | text | photo
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seekBlocked, setSeekBlocked] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageExtracted, setImageExtracted] = useState(false);
  const [detected, setDetected] = useState(null); // {key, label, reliable}
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const tl = TAB_LABELS[lang] || TAB_LABELS.en;

  // Debounced platform detection when URL changes
  React.useEffect(() => {
    if (!jobUrl || jobUrl.length < 10 || !/^https?:\/\//i.test(jobUrl)) {
      setDetected(null);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/detect-job-platform`, { url: jobUrl });
        setDetected(r.data);
      } catch {
        setDetected(null);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [jobUrl]);

  const fetchJob = async () => {
    if (!jobUrl) return;
    setLoading(true);
    setError("");
    setSeekBlocked(false);
    try {
      const res = await axios.post(`${API}/fetch-seek`, { url: jobUrl });
      setJobText(res.data.combined_text);
      setJobMeta({
        title: res.data.title,
        company: res.data.company,
        location: res.data.location,
      });
      // Auto-switch to text tab so user sees the loaded content
      setTab("text");
    } catch (e) {
      const detail = (e?.response?.data?.detail || "").toLowerCase();
      // Detect any "blocked / paste / could not extract" scenario → friendly UX
      if (detail.includes("blocking") ||
          detail.includes("paste") ||
          detail.includes("automated requests") ||
          detail.includes("could not extract") ||
          detail.includes("could not fetch") ||
          detail.includes("could not reach")) {
        setSeekBlocked(true);
        setError("");
        setTab("text");
        setTimeout(() => {
          textareaRef.current?.focus();
          textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      } else {
        setError(e?.response?.data?.detail || t.errors.seek);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    setError("");
    setImageExtracted(false);

    // Client-side validation
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError(lang === "fr" ? "Seules les images JPG, PNG ou WebP sont acceptées." : "Only JPG, PNG or WebP images are accepted.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(lang === "fr" ? "Image trop lourde (max 10 Mo)." : "Image too large (max 10 MB).");
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/parse-job-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const { combined_text, title, company, location } = res.data;
      setJobText(combined_text || "");
      setJobMeta({ title: title || "", company: company || "", location: location || "" });
      setImageExtracted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || (lang === "fr" ? "Impossible d'extraire le texte de l'image." : "Could not extract text from image."));
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageExtracted(false);
    setJobText("");
    setJobMeta({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageFile(file);
  };

  const tabs = [
    { key: "url", label: tl.url, icon: LinkIcon, hint: tl.url_hint },
    { key: "text", label: tl.text, icon: Type, hint: tl.text_hint },
    { key: "photo", label: tl.photo, icon: Camera, hint: tl.photo_hint, badge: "NEW" },
  ];

  return (
    <div data-testid="wizard-step-2">
      <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#292524] mb-2">{t.wizard.s2.title}</h2>
      <p className="text-[#57534E] mb-6">{t.wizard.s2.sub}</p>

      {/* Blocked banner (global — visible across all tabs since fetchJob auto-switches to Paste text) */}
      {seekBlocked && (
        <div className="mb-4 p-4 rounded-2xl border-2 border-[#E5A93D] bg-[#FFF8EB] flex items-start gap-3" data-testid="seek-blocked-banner">
          <AlertTriangle className="w-5 h-5 text-[#E5A93D] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#57534E] leading-relaxed">
            <strong className="text-[#9B3B1E]">{tl.blocked_title}</strong>
            <br />
            {tl.blocked_body}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[#F2EBE1] rounded-2xl mb-4" data-testid="job-import-tabs">
        {tabs.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all relative ${active ? "bg-white text-[#292524] shadow-sm" : "text-[#78716C] hover:text-[#292524]"}`}
              data-testid={`job-tab-${tb.key}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{tb.label}</span>
              {tb.badge && !active && (
                <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-[#D95D39] text-white text-[8px] font-black tracking-wider">
                  {tb.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="text-xs text-[#78716C] mb-6 pl-1">
        {tabs.find((tb) => tb.key === tab)?.hint}
      </div>

      {/* URL tab */}
      {tab === "url" && (
        <div data-testid="job-tab-content-url">
          <label className="block mb-2 font-semibold text-[#292524]">{tl.url_label || "Job URL"}</label>
          <div className="flex gap-2 flex-col sm:flex-row">
            <input
              className="input flex-1"
              placeholder={tl.url_placeholder}
              value={jobUrl}
              onChange={(e) => setJobUrl(e.target.value)}
              data-testid="seek-url-input"
            />
            <button
              onClick={fetchJob}
              disabled={!jobUrl || loading}
              className="btn-secondary"
              data-testid="seek-fetch-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
              {loading ? t.wizard.s2.fetching : t.wizard.s2.fetch}
            </button>
          </div>

          {/* Auto-detected platform tag */}
          {detected && detected.key !== "unknown" && !jobMeta?.title && (
            <div className="mt-2 text-xs flex items-center gap-1.5" data-testid="platform-detected">
              {detected.reliable ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3B8268]/15 text-[#3B8268] font-semibold">
                  <Check className="w-3 h-3" /> {detected.label} — {tl.platform_reliable}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E5A93D]/15 text-[#9B7A1E] font-semibold">
                  <AlertTriangle className="w-3 h-3" /> {detected.label} — {tl.platform_may_block}
                </span>
              )}
            </div>
          )}

          {/* Supported platforms list */}
          <div className="mt-3 text-[11px] text-[#78716C]" data-testid="supported-platforms">
            <span className="font-semibold">{tl.supported}:</span>{" "}
            <span>Seek · Indeed · Jora · Backpacker Job Board · Workforce Australia · CareerOne · Gumtree</span>
          </div>

          {jobMeta?.title && (
            <div className="mt-3 inline-flex items-center gap-2 tag tag-success" data-testid="job-loaded-tag">
              <Check className="w-3.5 h-3.5" /> {t.wizard.s2.loaded}: {jobMeta.title} {jobMeta.company && `— ${jobMeta.company}`}
            </div>
          )}
        </div>
      )}

      {/* Text tab */}
      {tab === "text" && (
        <div data-testid="job-tab-content-text">
          <textarea
            ref={textareaRef}
            className="input min-h-[220px]"
            placeholder={t.wizard.s2.text_placeholder}
            value={jobText}
            onChange={(e) => setJobText(e.target.value)}
            data-testid="job-text-input"
          />
          {jobMeta?.title && (
            <div className="mt-3 inline-flex items-center gap-2 tag tag-success">
              <Check className="w-3.5 h-3.5" /> {jobMeta.title} {jobMeta.company && `— ${jobMeta.company}`}
            </div>
          )}
        </div>
      )}

      {/* Photo tab */}
      {tab === "photo" && (
        <div data-testid="job-tab-content-photo">
          {!imagePreview && !imageExtracted ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border-2 border-dashed border-[#D95D39]/40 bg-[#FDF7F3] rounded-2xl p-8 sm:p-12 text-center cursor-pointer hover:border-[#D95D39] hover:bg-[#FDEEEC] transition-colors"
              data-testid="job-image-dropzone"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#D95D39]/15 mx-auto mb-3 flex items-center justify-center">
                <Camera className="w-7 h-7 text-[#D95D39]" />
              </div>
              <div className="font-heading font-bold text-lg text-[#292524] mb-1">{tl.photo_cta}</div>
              <div className="text-xs text-[#78716C]">{tl.photo_drop}</div>
              <div className="text-[10px] text-[#78716C] mt-3 font-semibold">{tl.photo_types}</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleImageFile(e.target.files?.[0])}
                data-testid="job-image-input"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden border border-[#EAE5DB] bg-[#FAF6F0]" data-testid="job-image-preview">
                {imagePreview && (
                  <img src={imagePreview} alt="Job posting" className="w-full max-h-72 object-contain" />
                )}
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center backdrop-blur"
                  aria-label="Remove image"
                  data-testid="job-image-clear"
                >
                  <X className="w-4 h-4" />
                </button>
                {loading && (
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 text-[#D95D39] animate-spin mb-2" />
                    <div className="text-sm font-bold text-[#292524]">{tl.photo_extracting}</div>
                  </div>
                )}
              </div>

              {imageExtracted && !loading && (
                <>
                  <div className="p-3 rounded-xl bg-[#3B8268]/10 border border-[#3B8268]/30 flex items-start gap-2 text-sm" data-testid="job-image-success">
                    <Sparkles className="w-4 h-4 text-[#3B8268] flex-shrink-0 mt-0.5" />
                    <div className="text-[#3B8268]">{tl.photo_success}</div>
                  </div>
                  {jobMeta?.title && (
                    <div className="text-xs text-[#78716C]">
                      <strong className="text-[#292524]">{jobMeta.title}</strong>
                      {jobMeta.company && ` — ${jobMeta.company}`}
                      {jobMeta.location && ` · ${jobMeta.location}`}
                    </div>
                  )}
                  <textarea
                    className="input min-h-[180px]"
                    value={jobText}
                    onChange={(e) => setJobText(e.target.value)}
                    data-testid="job-image-extracted-text"
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 text-sm text-[#D9423E] flex items-center gap-2" data-testid="seek-fetch-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
    </div>
  );
}
